import { useState } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";
import { UniformLogo } from "src/uniform/contenttypes/logo";

import VideoPauseOverlayIcon from "@atlaskit/icon/core/video-pause-overlay";
import VideoPlayOverlayIcon from "@atlaskit/icon/core/video-play-overlay";
import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";

import { Image } from "@/libraries/design/components";
import { wacToken, ColumnLayout } from "@/libraries/design/foundations";

export interface MarqueeProps {
    mobileLogos?: UniformLogo[];
    tabletLogos?: UniformLogo[];
    desktopLogos: UniformLogo[];
}

export interface LogoProps extends UniformLogo {
    ariaHidden: boolean;
    tabIndex: 0 | -1;
}

const Logo = ({ src, href, alt, ariaHidden, tabIndex }: LogoProps) => {
    return (
        <li aria-hidden={ariaHidden}>
            <a
                href={href}
                tabIndex={tabIndex}
                css={linkCss}
                data-event="clicked"
                data-event-component="linkButton"
                data-event-container="connector"
            >
                <div css={logoCss}>
                    <Image
                        src={src}
                        alt={alt}
                        style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "contain"
                        }}
                    />
                </div>
            </a>
        </li>
    );
};

export const LogosMarquee = ({
    mobileLogos,
    tabletLogos,
    desktopLogos
}: MarqueeProps) => {
    const [running, setRunning] = useState(true);

    tabletLogos = tabletLogos?.length === 0 ? desktopLogos : tabletLogos;
    mobileLogos = mobileLogos?.length === 0 ? desktopLogos : mobileLogos;

    // @ts-ignore
    const mobileLogosLength = mobileLogos.length;
    // @ts-ignore
    const tabletLogosLength = tabletLogos.length;
    const desktopLogosLength = desktopLogos.length;

    mobileLogos = [
        // @ts-ignore
        ...mobileLogos,
        ...mobileLogos,
        ...mobileLogos,
        ...mobileLogos
    ];
    tabletLogos = [
        ...tabletLogos,
        ...tabletLogos,
        ...tabletLogos,
        ...tabletLogos
    ];
    desktopLogos = [
        ...desktopLogos,
        ...desktopLogos,
        ...desktopLogos,
        ...desktopLogos
    ];

    const intl = useIntl();

    return (
        <div css={marqueeContainerCss}>
            <ColumnLayout layout="12">
                <div css={marqueeButtonContainerCss}>
                    <button
                        css={marqueeButtonCss}
                        onClick={() => setRunning((r) => !r)}
                        data-event="clicked"
                        data-event-component="button"
                        data-event-container="connector"
                    >
                        <div css={buttonCirlceCss}>
                            {running ? (
                                <VideoPauseOverlayIcon
                                    label={intl.formatMessage({
                                        id: "connector-logo-marquee.pause",
                                        defaultMessage: "Pause"
                                    })}
                                />
                            ) : (
                                <VideoPlayOverlayIcon
                                    label={intl.formatMessage({
                                        id: "connector-logo-marquee.play",
                                        defaultMessage: "Play"
                                    })}
                                />
                            )}
                        </div>
                    </button>
                </div>
            </ColumnLayout>
            <div css={listContainerCss}>
                <ul
                    css={[
                        animationKeyframesCss,
                        listCss,
                        css({
                            animation: "scroll 18s linear infinite",
                            animationPlayState: running ? "running" : "paused"
                        }),
                        pauseOnHoverCss,
                        mobileCss
                    ]}
                >
                    {mobileLogos.map((logo, index) => {
                        const isOriginal = index < mobileLogosLength;
                        return (
                            <Logo
                                key={index}
                                ariaHidden={!isOriginal}
                                tabIndex={isOriginal ? 0 : -1}
                                {...logo}
                            />
                        );
                    })}
                </ul>
                <ul
                    css={[
                        animationKeyframesCss,
                        listCss,
                        css({
                            animation: "scroll 36s linear infinite",
                            animationPlayState: running ? "running" : "paused"
                        }),
                        pauseOnHoverCss,
                        tabletCss
                    ]}
                >
                    {tabletLogos.map((logo, index) => {
                        const isOriginal = index < tabletLogosLength;
                        return (
                            <Logo
                                key={index}
                                ariaHidden={!isOriginal}
                                tabIndex={isOriginal ? 0 : -1}
                                {...logo}
                            />
                        );
                    })}
                </ul>
                <ul
                    css={[
                        animationKeyframesCss,
                        listCss,
                        css({
                            animation: "scroll 90s linear infinite",
                            animationPlayState: running ? "running" : "paused"
                        }),
                        pauseOnHoverCss,
                        desktopCss
                    ]}
                >
                    {desktopLogos.map((logo, index) => {
                        const isOriginal = index < desktopLogosLength;
                        return (
                            <Logo
                                key={index}
                                ariaHidden={!isOriginal}
                                tabIndex={isOriginal ? 0 : -1}
                                {...logo}
                            />
                        );
                    })}
                </ul>
            </div>
        </div>
    );
};

const linkCss = css({
    "display": "flex",
    "backgroundColor": wacToken("color.neutral.dark100"),
    "borderRadius": wacToken("corner.radius.card"),

    "&:hover": {
        backgroundColor: wacToken("color.neutral.dark300")
    }
});

const logoCss = css({
    height: "60px",
    width: "60px",
    margin: "10px"
});

const listCss = css({
    listStyle: "none",
    padding: 0,
    margin: 0,
    display: "flex",
    gap: token("space.250"),
    width: "fit-content"
});

const marqueeContainerCss = css({
    overflow: "hidden",
    position: "relative",
    display: "flex",
    flexDirection: "column",
    gap: token("space.150")
});

const marqueeButtonContainerCss = css({
    width: "100%",
    display: "flex",
    justifyContent: "end"
});

const marqueeButtonCss = css({
    height: "44px",
    width: "44px",
    backgroundColor: "transparent",
    border: "none",
    borderRadius: "100%",

    ["&:hover"]: {
        cursor: "pointer"
    }
});

const buttonCirlceCss = css({
    height: "32px",
    width: "32px",
    backgroundColor: wacToken("color.neutral.dark400"),
    border: "none",
    borderRadius: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",

    ["&:hover"]: {
        cursor: "pointer",
        backgroundColor: wacToken("color.neutral.dark500")
    },

    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    span: {
        color: `${wacToken("color.neutral.dark1000")} !important`
    }
});

const listContainerCss = css({
    width: "100%",
    maxWidth: "4000px",
    margin: "auto",
    overflowX: "hidden"
});

const animationKeyframesCss = css({
    width: "100%",
    whiteSpace: "nowrap",

    ["@keyframes scroll"]: {
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        from: { transform: "translateX(0%)" },
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        to: { transform: "translateX(calc(-50% - 10px))" }
    }
});

const pauseOnHoverCss = css({
    ["&:hover"]: {
        animationPlayState: "paused"
    }
});

const desktopCss = css({
    [media.below.md]: {
        display: "none"
    }
});

const tabletCss = css({
    [media.above.md]: {
        display: "none"
    },
    [media.below.sm]: {
        display: "none"
    }
});

const mobileCss = css({
    [media.above.sm]: {
        display: "none"
    }
});
